import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Mic, Loader2, Clock, Shuffle, Sparkles, AlertCircle, Plus, X, AlarmClock, Hand } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isPushSubscribed } from "@/lib/pushManager";

const WEEKDAYS = [
  { id: 0, label: "Lun" },
  { id: 1, label: "Mar" },
  { id: 2, label: "Mer" },
  { id: 3, label: "Gio" },
  { id: 4, label: "Ven" },
  { id: 5, label: "Sab" },
  { id: 6, label: "Dom" },
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const VoiceCheckInScheduleCard = () => {
  const [hasAccess, setHasAccess] = useState(null);
  const [enabled, setEnabled] = useState(false);

  // 2026-05-13: 3 independent EMA sampling modes
  const [randomEnabled, setRandomEnabled] = useState(true);
  const [fixedEnabled, setFixedEnabled] = useState(false);
  const [allowSelfInitiated, setAllowSelfInitiated] = useState(true);

  const [promptsPerDay, setPromptsPerDay] = useState(5);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(21);
  const [minGap, setMinGap] = useState(60);
  const [activeWeekdays, setActiveWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);

  const [fixedTimes, setFixedTimes] = useState([]);
  const [newTime, setNewTime] = useState("");

  const [saving, setSaving] = useState(false);
  const [todayPrompts, setTodayPrompts] = useState([]);
  const [pushReady, setPushReady] = useState(null);

  useEffect(() => {
    checkAccessAndLoad();
    isPushSubscribed().then(setPushReady).catch(() => setPushReady(false));
  }, []);

  const checkAccessAndLoad = async () => {
    try {
      const accessRes = await api.get("/voice-mood/access");
      setHasAccess(accessRes.data.has_access);
      if (accessRes.data.has_access) {
        const schedRes = await api.get("/voice-mood/schedule");
        const d = schedRes.data;
        setEnabled(d.enabled ?? false);
        setRandomEnabled(d.random_enabled ?? true);
        setFixedEnabled(d.fixed_enabled ?? false);
        setAllowSelfInitiated(d.allow_self_initiated ?? true);
        setFixedTimes(Array.isArray(d.times) ? d.times : []);
        setPromptsPerDay(d.prompts_per_day ?? 5);
        setStartHour(d.start_hour ?? 9);
        setEndHour(d.end_hour ?? 21);
        setMinGap(d.min_gap_minutes ?? 60);
        setActiveWeekdays(d.active_weekdays ?? [0, 1, 2, 3, 4, 5, 6]);
        if (d.enabled) loadTodayPrompts();
      }
    } catch {
      setHasAccess(false);
    }
  };

  const loadTodayPrompts = async () => {
    try {
      const res = await api.get("/voice-mood/today-prompts");
      setTodayPrompts(res.data.prompts || []);
    } catch {
      setTodayPrompts([]);
    }
  };

  const persist = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = {
        enabled,
        random_enabled: randomEnabled,
        fixed_enabled: fixedEnabled,
        allow_self_initiated: allowSelfInitiated,
        times: fixedTimes,
        prompts_per_day: promptsPerDay,
        start_hour: startHour,
        end_hour: endHour,
        min_gap_minutes: minGap,
        active_weekdays: activeWeekdays,
        ...overrides,
      };
      await api.post("/voice-mood/schedule", payload);
      toast.success(payload.enabled ? "Impostazioni salvate" : "Promemoria disattivati");
      if (payload.enabled && (payload.random_enabled || payload.fixed_enabled)) {
        await loadTodayPrompts();
      } else {
        setTodayPrompts([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleMasterToggle = (checked) => {
    setEnabled(checked);
    persist({ enabled: checked });
  };

  const handleRandomToggle = (checked) => {
    setRandomEnabled(checked);
    persist({ random_enabled: checked });
  };

  const handleFixedToggle = (checked) => {
    if (checked && fixedTimes.length === 0) {
      toast.error("Aggiungi almeno un orario fisso prima di attivare la modalità");
      return;
    }
    setFixedEnabled(checked);
    persist({ fixed_enabled: checked });
  };

  const handleSelfInitiatedToggle = (checked) => {
    setAllowSelfInitiated(checked);
    persist({ allow_self_initiated: checked });
  };

  const handleWeekdayToggle = (day) => {
    const updated = activeWeekdays.includes(day)
      ? activeWeekdays.filter((d) => d !== day)
      : [...activeWeekdays, day].sort();
    if (updated.length === 0) {
      toast.error("Seleziona almeno un giorno");
      return;
    }
    setActiveWeekdays(updated);
    persist({ active_weekdays: updated });
  };

  const addFixedTime = () => {
    const t = newTime.trim();
    if (!TIME_RE.test(t)) {
      toast.error("Formato non valido: usa HH:MM (es. 09:00)");
      return;
    }
    if (fixedTimes.includes(t)) {
      toast.error("Orario già presente");
      return;
    }
    const updated = [...fixedTimes, t].sort();
    setFixedTimes(updated);
    setNewTime("");
    persist({ times: updated });
  };

  const removeFixedTime = (t) => {
    const updated = fixedTimes.filter((x) => x !== t);
    setFixedTimes(updated);
    // If we just removed the last one, also disable fixed mode so cron doesn't 400
    const overrides = { times: updated };
    if (updated.length === 0 && fixedEnabled) {
      setFixedEnabled(false);
      overrides.fixed_enabled = false;
    }
    persist(overrides);
  };

  if (hasAccess === null || hasAccess === false) return null;

  const rangeHours = endHour - startHour;
  const minHoursNeeded = ((promptsPerDay - 1) * minGap) / 60;
  const rangeOk = rangeHours >= minHoursNeeded;

  // Split today's prompts by sampling_type for clearer display
  const randomPromptsToday = todayPrompts.filter((p) => (p.sampling_type ?? "random") === "random");
  const scheduledPromptsToday = todayPrompts.filter((p) => p.sampling_type === "scheduled");

  return (
    <Card className="border border-border shadow-sm rounded-sm" data-testid="voice-schedule-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
          <Mic className="w-5 h-5" strokeWidth={1.5} />
          Check-in
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs sm:text-sm text-muted-foreground">
          Tre modi per registrare un check-in: a sorpresa (sondaggi casuali nella
          giornata), a orari fissi e quando ti va (check-in spontanei).
        </p>

        {/* Push not enabled warning */}
        {pushReady === false && enabled && (randomEnabled || fixedEnabled) && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <strong>Notifiche push non attive su questo dispositivo.</strong>{" "}
              Attivale dalla card "Notifiche Push" in basso, altrimenti i promemoria non arriveranno.
            </div>
          </div>
        )}

        {/* MASTER toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{enabled ? "Tracking attivo" : "Tracking disattivato"}</p>
            <p className="text-xs text-muted-foreground">
              {enabled ? "Le modalità abilitate qui sotto sono operative" : "Attiva per usare le modalità EMA"}
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleMasterToggle}
            disabled={saving}
            data-testid="voice-schedule-toggle"
          />
        </div>

        {enabled && (
          <div className="space-y-4 pt-2 border-t">
            {/* ===== Mode 1: Random (signal-contingent) ===== */}
            <section className="space-y-3" data-testid="ema-random-section">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-2 min-w-0">
                  <Shuffle className="w-4 h-4 mt-0.5 text-rose-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Ping random</p>
                    <p className="text-[11px] text-muted-foreground">
                      Notifiche in orari casuali (riduce il bias di selezione)
                    </p>
                  </div>
                </div>
                <Switch
                  checked={randomEnabled}
                  onCheckedChange={handleRandomToggle}
                  disabled={saving}
                  data-testid="ema-random-toggle"
                />
              </div>

              {randomEnabled && (
                <div className="pl-6 space-y-3">
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">
                      Prompt al giorno: <span className="text-rose-600 font-semibold">{promptsPerDay}</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="15"
                      step="1"
                      value={promptsPerDay}
                      onChange={(e) => setPromptsPerDay(Number(e.target.value))}
                      onMouseUp={() => persist({ prompts_per_day: promptsPerDay })}
                      onTouchEnd={() => persist({ prompts_per_day: promptsPerDay })}
                      className="w-full accent-rose-500"
                      data-testid="prompts-per-day-slider"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground px-1 mt-0.5">
                      <span>1</span><span>4</span><span>8</span><span>12</span><span>15</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Inizio</label>
                      <select
                        value={startHour}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setStartHour(v);
                          persist({ start_hour: v });
                        }}
                        className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="start-hour-select"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={`start-${i}`} value={i} disabled={i >= endHour}>
                            {i.toString().padStart(2, "0")}:00
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Fine</label>
                      <select
                        value={endHour}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setEndHour(v);
                          persist({ end_hour: v });
                        }}
                        className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="end-hour-select"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={`end-${i}`} value={i} disabled={i <= startHour}>
                            {i.toString().padStart(2, "0")}:00
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium mb-1.5 block">
                      Distanza minima tra prompt: <span className="text-rose-600 font-semibold">{minGap} min</span>
                    </label>
                    <input
                      type="range"
                      min="30"
                      max="180"
                      step="15"
                      value={minGap}
                      onChange={(e) => setMinGap(Number(e.target.value))}
                      onMouseUp={() => persist({ min_gap_minutes: minGap })}
                      onTouchEnd={() => persist({ min_gap_minutes: minGap })}
                      className="w-full accent-rose-500"
                      data-testid="min-gap-slider"
                    />
                  </div>

                  {!rangeOk && (
                    <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>Con {promptsPerDay} prompt e {minGap}min di distanza servono almeno {Math.ceil(minHoursNeeded)}h. Amplia l'intervallo orario.</span>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Giorni attivi</label>
                    <div className="grid grid-cols-7 gap-1">
                      {WEEKDAYS.map((w) => {
                        const active = activeWeekdays.includes(w.id);
                        return (
                          <button
                            key={w.id}
                            onClick={() => handleWeekdayToggle(w.id)}
                            disabled={saving}
                            className={`py-1.5 rounded-md text-[11px] font-medium border transition-colors ${
                              active
                                ? "bg-rose-500 text-white border-rose-500"
                                : "bg-white text-muted-foreground border-muted hover:border-rose-200"
                            }`}
                            data-testid={`weekday-${w.id}`}
                          >
                            {w.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {randomPromptsToday.length > 0 && (
                    <div className="pt-1" data-testid="today-prompts-preview">
                      <div className="flex items-center gap-1.5 text-xs font-medium mb-2">
                        <Sparkles className="w-3.5 h-3.5 text-rose-500" />
                        Orari random generati per oggi
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {randomPromptsToday.map((p) => (
                          <Badge
                            key={`r-${p.time}`}
                            variant={p.sent ? "secondary" : "outline"}
                            className={`text-[11px] py-1 px-2 gap-1 ${p.sent ? "opacity-50" : "border-rose-300 text-rose-700"}`}
                          >
                            <Clock className="w-3 h-3" />
                            {p.time}
                            {p.sent && <span className="text-[9px]">✓</span>}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        Gli orari cambiano ogni giorno. Una nuova sequenza casuale viene generata a mezzanotte.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* ===== Mode 2: Fixed times (interval-contingent) ===== */}
            <section className="space-y-3 pt-2 border-t" data-testid="ema-fixed-section">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-2 min-w-0">
                  <AlarmClock className="w-4 h-4 mt-0.5 text-indigo-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Orari fissi</p>
                    <p className="text-[11px] text-muted-foreground">
                      Notifiche agli stessi orari ogni giorno (utile per pattern circadiani)
                    </p>
                  </div>
                </div>
                <Switch
                  checked={fixedEnabled}
                  onCheckedChange={handleFixedToggle}
                  disabled={saving}
                  data-testid="ema-fixed-toggle"
                />
              </div>

              <div className="pl-6 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm flex-1"
                    data-testid="fixed-time-input"
                  />
                  <button
                    onClick={addFixedTime}
                    disabled={saving || !newTime}
                    className="h-9 px-3 rounded-md bg-indigo-500 text-white text-xs font-medium hover:bg-indigo-600 disabled:opacity-50 flex items-center gap-1"
                    data-testid="fixed-time-add"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi
                  </button>
                </div>
                {fixedTimes.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic">Nessun orario configurato.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid="fixed-times-list">
                    {fixedTimes.map((t) => {
                      const sentToday = scheduledPromptsToday.some((p) => p.time === t && p.sent);
                      return (
                        <Badge
                          key={`f-${t}`}
                          variant="outline"
                          className={`text-[11px] py-1 px-2 gap-1 ${sentToday ? "opacity-50" : "border-indigo-300 text-indigo-700"}`}
                        >
                          <Clock className="w-3 h-3" />
                          {t}
                          {sentToday && <span className="text-[9px]">✓</span>}
                          <button
                            onClick={() => removeFixedTime(t)}
                            disabled={saving}
                            className="ml-0.5 hover:text-rose-600"
                            data-testid={`fixed-time-remove-${t}`}
                            aria-label={`Rimuovi ${t}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* ===== Mode 3: Self-initiated (event-contingent) ===== */}
            <section className="space-y-2 pt-2 border-t" data-testid="ema-self-section">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-2 min-w-0">
                  <Hand className="w-4 h-4 mt-0.5 text-teal-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Check-in spontanei</p>
                    <p className="text-[11px] text-muted-foreground">
                      Mostra il pulsante "+ Nuovo Check-in" per registrare quando vuoi
                    </p>
                  </div>
                </div>
                <Switch
                  checked={allowSelfInitiated}
                  onCheckedChange={handleSelfInitiatedToggle}
                  disabled={saving}
                  data-testid="ema-self-toggle"
                />
              </div>
            </section>
          </div>
        )}

        {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </CardContent>
    </Card>
  );
};
